import { faCamera, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { Fragment, useEffect } from 'react';
import { RowRecord, TableColumn } from 'react-data-table-component';
import { FormattedMessage, useIntl } from 'react-intl';
import { FlexboxGrid, Message, Modal } from 'rsuite';
import Camera from '../../../handlers/Camera/Camera';
import ipCameraHistoryHandler from '../../../handlers/history/IPCameraHistoryHandler';
import { authHeader } from '../../../redux/helpers';
import { axiosService } from '../../../redux/services';
import HistorySortPart from '../../History/SortPart';
import IpCameraDescCell from '../../History/Table/IpCameraDescCell';
import IpCameraOptionCell from '../../History/Table/IpCameraOptionCell';
import HistoryTable from '../../ReactDataTableComponent/HistoryTable';

type Props = {
    show: boolean;
    onHide: () => void;
    camera: Camera;
};

type FormValue = {
    user: string | null;
    before: Date | null;
    after: Date | null;
    nbElements: number;
};

type UserSelector = {
    label: string;
    value: string;
};

export const IpCameraHistoryModal = (props: Props) => {
    const intl = useIntl();

    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [history, setHistory] = React.useState<Array<any>>([]);
    const [hasError, setHasError] = React.useState<boolean>(false);
    const [users, setUsers] = React.useState<UserSelector[]>([]);
    const [formValues, setFormValues] = React.useState<FormValue>({
        user: null,
        before: null,
        after: null,
        nbElements: 100,
    });

    const columns: TableColumn<RowRecord>[] = [
        {
            name: intl.formatMessage({
                id: 'history.ipCamera.user',
            }),
            selector: 'user',
            sortable: true,
            cell: row => {
                if (row.user === 'MQTT') {
                    return <FontAwesomeIcon icon={faRobot} size="lg" />;
                } else {
                    return row.user;
                }
            },
        },
        {
            name: intl.formatMessage({
                id: 'history.ipCamera.createdAt',
            }),
            selector: 'createdAt',
            sortable: true,
        },
        {
            name: intl.formatMessage({
                id: 'history.ipCamera.ipCameraLabel',
            }),
            selector: 'ipCameraLabel',
            sortable: true,
        },
        {
            name: intl.formatMessage({
                id: 'history.ipCamera.desc',
            }),
            selector: 'desc',
            sortable: true,
            cell: row => IpCameraDescCell(row.desc),
        },
        {
            name: intl.formatMessage({
                id: 'history.ipCamera.option',
            }),
            selector: 'option',
            cell: row => IpCameraOptionCell(row.desc, row.option),
        },
    ];

    const loadHistory = () => {
        setIsLoading(true);
        setHasError(false);

        axiosService
            .getAxios()
            .post(
                '/historics/ip-camera',
                {
                    nbResult: formValues.nbElements || 100,
                    user: formValues.user,
                    id: props.camera.getId(),
                    before: formValues.before ? moment(formValues.before).toISOString() : null,
                    after: formValues.after ? moment(formValues.after).toISOString() : null,
                },
                {
                    headers: authHeader(),
                }
            )
            .then(ipCameraHistoryResponse => {
                let users: Array<string> = [];

                const histories = ipCameraHistoryResponse.data.map(history => {
                    const ipCamera = new ipCameraHistoryHandler(history);

                    if (!users.includes(history.user)) {
                        users.push(history.user);
                    }

                    return ipCamera.getHistory();
                });

                setHistory(histories);
                setUsers(users.map(user => ({ label: user, value: user })));
            })
            .catch(err => {
                console.error(err);

                setHasError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        setFormValues({
            user: null,
            before: null,
            after: null,
            nbElements: 100,
        });

        setAfter(null);
        setBefore(null);
        setNbElements(100);
        setUser(null);

        if (props.show) {
            loadHistory();
        }
    }, [props.show]);

    const setUser = user => {
        setFormValues({ ...formValues, user });
    };

    const setBefore = before => {
        setFormValues({ ...formValues, before });
    };

    const setAfter = after => {
        setFormValues({ ...formValues, after });
    };

    const setNbElements = nbElements => {
        setFormValues({ ...formValues, nbElements });
    };

    return (
        <Modal
            full
            backdrop="static"
            show={props.show}
            onHide={props.onHide}
            style={{ height: '95%' }}
            className="no-modal-padding">
            <Modal.Header style={{ backgroundColor: '#212121' }}>
                <HistorySortPart
                    users={users}
                    setUser={setUser}
                    setBefore={setBefore}
                    setAfter={setAfter}
                    valid={loadHistory}
                    setNbElements={setNbElements}
                />
            </Modal.Header>
            <Modal.Body>
                {hasError ? (
                    <Message type="error">
                        <FormattedMessage id="history.ipCanModules.fetchHistoryError" />
                    </Message>
                ) : (
                    <Fragment>
                        <FlexboxGrid align="middle" justify="center">
                            <FlexboxGrid.Item>
                                <FontAwesomeIcon icon={faCamera} size="2x" className="margin-right-5" />
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item>
                                <h4
                                    data-cy="history-controller-label-name"
                                    className="text-center">{`${props.camera.getName()} (${props.camera.getMacStr()})`}</h4>
                            </FlexboxGrid.Item>
                        </FlexboxGrid>

                        <HistoryTable
                            columns={columns}
                            data={history}
                            progressPending={isLoading}
                            expandableRows
                            expandOnRowClicked
                            expandableRowDisabled={row => row.disabled}
                        />
                    </Fragment>
                )}
            </Modal.Body>
        </Modal>
    );
};
